import { Box, Divider, useTheme } from "@mui/material";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { MyMealsQuantitySelectionMode } from "../../Account/Subscriptions/subscriptionsUtils/subscriptions-types";
import useMealCount from "../hooks/useMealCount";
import "../utils/myMealsCSS.css";
import { areDayMealsModifiable } from "../utils/permissions";
import { MealRow } from "./MealRow";
import { MealRowHeader } from "./MealRowHeader";
import { ProductSection } from "./ProductSection";

MealRowSection.propTypes = {
  day: PropTypes.object.isRequired,
  days: PropTypes.array.isRequired,
  skippedDays: PropTypes.array.isRequired,
  recipeImageBaseUrl: PropTypes.string.isRequired,
  productImageBaseUrl: PropTypes.string.isRequired,
  selectedOrderOmissions: PropTypes.array.isRequired,
  selectedOrderRecipeType: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
  }),
  handleRecipeIncrement: PropTypes.func.isRequired,
  handleRecipeDecrement: PropTypes.func.isRequired,
  handleProductQuantityChange: PropTypes.func.isRequired,
  handleMealQuantityChange: PropTypes.func.isRequired,
  culture: PropTypes.object,
  myMealsConfig: PropTypes.object,
  productAndRecipeCardLayoutSettings: PropTypes.object,
  handleRecipeToggle: PropTypes.func,
  mealQuantityPerDay: PropTypes.object,
  amountsPerDay: PropTypes.object,
  amountForOrder: PropTypes.number,
  updateAmountsPerDay: PropTypes.func,
  updateAmountForOrder: PropTypes.func,
  updateMealQuantityPerDay: PropTypes.func,
  selectedOrderMealQuantity: PropTypes.number,
  updateSelectedOrderMealQuantity: PropTypes.func,
  updateMealPropertiesPerDay: PropTypes.func,
  totalQuantitiesPerDay: PropTypes.object,
  updateTotalQuantitiesPerDay: PropTypes.func,
  orderType: PropTypes.string,
  symbolFlags: PropTypes.array,
  mealToEdit: PropTypes.object,
  handleEditMealClick: PropTypes.func,
  recipeIngredientReplacementMode: PropTypes.number,
  handleCustomizeChange: PropTypes.func,
  handleCustomizeToggle: PropTypes.func,
  alwaysPromptCustomizeMealModal: PropTypes.bool,
  handleSubmit: PropTypes.func,
};

export default function MealRowSection(props) {
  const { spacing } = useTheme();
  const [mealsWithSelectionsIds, setMealsWithSelectionsIds] = useState({});
  const [extraMealsIds, setExtraMealsIds] = useState({});
  const [mealsWithoutSelectionsIds, setMealsWithoutSelectionsIds] = useState({});
  const {
    myMealsConfig: { RestrictChoosingRecipes, RestrictAddRemoveDays, ProductsHeadingText },
  } = props;

  const { getRecipesForMeal, getSelectedRecipeCount, isMealQuantityFilled } = useMealCount();

  useEffect(() => {
    let getMealIdsWithSelections = (meals) => {
      if (!meals) {
        return null;
      }

      return meals
        .filter((meal) => {
          let recipesForMeal = props.day.Recipes.filter((recipe) => recipe.ProgramMealId === meal.ProgramMealId);

          return meal.Quantity > 0 && recipesForMeal.some((recipe) => recipe.Quantity > 0);
        })
        .map((meal) => meal.ProgramMealId);
    };

    if (!mealsWithSelectionsIds[props.day.DateFormatted]) {
      setMealsWithSelectionsIds({
        ...mealsWithSelectionsIds,
        [props.day.DateFormatted]: getMealIdsWithSelections(props.day ? props.day.Meals : null),
      });
    }
  }, [props.day, mealsWithSelectionsIds]);

  useEffect(() => {
    let getMealIdsWithoutSelections = (meals) => {
      if (!meals) {
        return null;
      }

      return meals
        .filter((meal) => {
          let recipesForMeal = props.day.Recipes.filter((recipe) => recipe.ProgramMealId === meal.ProgramMealId);

          return meal.Quantity > 0 && !recipesForMeal.some((recipe) => recipe.Quantity > 0);
        })
        .map((meal) => meal.ProgramMealId);
    };

    if (!mealsWithoutSelectionsIds[props.day.DateFormatted]) {
      setMealsWithoutSelectionsIds({
        ...mealsWithoutSelectionsIds,
        [props.day.DateFormatted]: getMealIdsWithoutSelections(props.day ? props.day.Meals : null),
      });
    }
  }, [props.day, mealsWithoutSelectionsIds]);

  useEffect(() => {
    let extraMealsIdsTemp = props.day
      ? props.day.Meals.filter((meal) => meal.Quantity === 0).map((meal) => meal.ProgramMealId)
      : null;

    if (!extraMealsIds[props.day.DateFormatted]) {
      setExtraMealsIds({
        ...extraMealsIds,
        [props.day.DateFormatted]: extraMealsIdsTemp,
      });
    }
  }, [props.day, extraMealsIds]);

  const renderMealRows = (meals) => {
    return meals.map((meal) => {
      const recipes = getRecipesForMeal(
        props.day && props.day.Recipes,
        meal,
        props.myMealsConfig.DisplayRecipesByRecipeType,
        props.selectedOrderRecipeType
      );

      const isFull = isMealQuantityFilled(meal.Quantity, recipes && recipes);

      let noRecipes = "";
      if (Object.keys(recipes).length === 0) {
        noRecipes = "no-meals";
      }

      if (props.myMealsConfig.DisplayRecipesByMealType && meal.Quantity === 0) {
        noRecipes = "no-meals";
      }

      let filled = isFull && isFull ? "filled" : "";

      let selectedRecipes = getSelectedRecipeCount(recipes) || 0;

      const showIconButton =
        !allowAdHocChanges &&
        areDayMealsModifiable(props.day.Config) &&
        !restrictMealQuantitySelection &&
        !RestrictAddRemoveDays;

      return (
        <Box
          key={meal.ProgramMealId}
          mt={4}
          id={"ProgramMeal-" + meal.ProgramMealId}
          p={noRecipes === "no-meals" ? spacing(2, 2.5) : spacing(0, 0, 0, 1)}
          minHeight={noRecipes === "no-meals" ? "1px" : "initial"}
          sx={{ backgroundColor: noRecipes === "no-meals" ? "#f7f7f7" : "initial" }}
        >
          <MealRowHeader
            allowAdHocChanges={allowAdHocChanges}
            day={props.day}
            filled={filled}
            handleEditMealClick={props.handleEditMealClick}
            meal={meal}
            myMealsConfig={props.myMealsConfig}
            noRecipes={noRecipes === "no-meals"}
            restrictAddRemoveDays={RestrictAddRemoveDays}
            restrictMealQuantitySelection={restrictMealQuantitySelection}
            selectedRecipes={selectedRecipes}
            showIconButton={showIconButton}
          />

          <MealRow
            day={props.day}
            days={props.days}
            recipes={recipes}
            recipeImageBaseUrl={props.recipeImageBaseUrl}
            selectedOrderOmissions={props.selectedOrderOmissions}
            handleRecipeIncrement={props.handleRecipeIncrement}
            handleRecipeDecrement={props.handleRecipeDecrement}
            handleRecipeToggle={props.handleRecipeToggle}
            dayConfig={props.day.Config}
            mealQuantity={meal.Quantity}
            selectedRecipeQuantity={selectedRecipes}
            myMealsConfig={props.myMealsConfig}
            productAndRecipeCardLayoutSettings={props.productAndRecipeCardLayoutSettings}
            symbolFlags={props.symbolFlags}
            recipeIngredientReplacementMode={props.recipeIngredientReplacementMode}
            handleCustomizeChange={props.handleCustomizeChange}
            handleCustomizeToggle={props.handleCustomizeToggle}
            handleSubmit={props.handleSubmit}
            allowAdHocChanges={allowAdHocChanges}
            alwaysPromptCustomizeMealModal={props.alwaysPromptCustomizeMealModal}
          />
        </Box>
      );
    });
  };

  const products = props.day && props.day.Products;
  const showProducts = props.day && props.day.ShowProducts;

  let mealsWithSelections = null;
  if (mealsWithSelectionsIds && mealsWithSelectionsIds[props.day.DateFormatted]) {
    mealsWithSelections = props.day.Meals.filter((meal) =>
      mealsWithSelectionsIds[props.day.DateFormatted].includes(meal.ProgramMealId)
    );
  }

  let mealsWithoutSelections = null;
  if (mealsWithoutSelectionsIds && mealsWithoutSelectionsIds[props.day.DateFormatted]) {
    mealsWithoutSelections = props.day.Meals.filter((meal) =>
      mealsWithoutSelectionsIds[props.day.DateFormatted].includes(meal.ProgramMealId)
    );
  }

  let extraMeals = null;
  if (extraMealsIds && extraMealsIds[props.day.DateFormatted]) {
    extraMeals = props.day.Meals.filter((meal) => extraMealsIds[props.day.DateFormatted].includes(meal.ProgramMealId));
  }

  const isDaySkipped = props.skippedDays?.some((skipDay) =>
    moment(skipDay.customerDate).utc().isSame(moment(props.day.DateFormatted).utc(), "day")
  );

  const isShipDaySkipped = props.skippedDays?.some((skipDay) =>
    moment(skipDay.customerDate).utc().isSame(moment(props.day.ShipDate).utc(), "day")
  );

  // with ad-hoc changes mode, meal quantity is edited directly via recipe cards and we hide the edit meal quantity button
  const allowAdHocChanges = props.myMealsConfig.MealQuantitySelectionMode === MyMealsQuantitySelectionMode.AdHocChanges;
  const restrictMealQuantitySelection =
    props.myMealsConfig.MealQuantitySelectionMode === MyMealsQuantitySelectionMode.NoChanges ||
    isDaySkipped ||
    isShipDaySkipped;
  const restrictProductQuantitySelection = isDaySkipped || isShipDaySkipped;

  return (
    <>
      {mealsWithSelections && !RestrictChoosingRecipes && (
        <Box className="subscribed-types">{renderMealRows(mealsWithSelections)}</Box>
      )}
      {mealsWithoutSelections && !RestrictChoosingRecipes && (
        <Box className="subscribed-types">{renderMealRows(mealsWithoutSelections)}</Box>
      )}
      {props.myMealsConfig.DisplayRecipesByMealType && (
        <Box className="products" id="ProgramMeal-PRODUCTS" mt={2.5}>
          {products && products.length > 0 && showProducts && (
            <>
              {!RestrictChoosingRecipes && <Divider className="products-separator" />}
              <ProductSection
                products={products}
                productImageBaseUrl={props.productImageBaseUrl}
                handleProductQuantityChange={props.handleProductQuantityChange}
                dayConfig={props.day.Config}
                culture={props.culture}
                productAndRecipeCardLayoutSettings={props.productAndRecipeCardLayoutSettings}
                headingText={ProductsHeadingText}
                restrictProductQuantitySelection={restrictProductQuantitySelection}
              />
              {!RestrictChoosingRecipes && <Divider className="products-separator" />}
            </>
          )}
        </Box>
      )}
      {extraMeals && !RestrictChoosingRecipes && (
        <Box className="non-subscribed-types">{renderMealRows(extraMeals)}</Box>
      )}
      {!props.myMealsConfig.DisplayRecipesByMealType && (
        <Box className="products" id="ProgramMeal-PRODUCTS">
          {products && products.length > 0 && showProducts && (
            <>
              {!RestrictChoosingRecipes && <Divider className="products-separator" />}
              <ProductSection
                products={products}
                productImageBaseUrl={props.productImageBaseUrl}
                handleProductQuantityChange={props.handleProductQuantityChange}
                dayConfig={props.day.Config}
                culture={props.culture}
                productAndRecipeCardLayoutSettings={props.productAndRecipeCardLayoutSettings}
                headingText={ProductsHeadingText}
                restrictProductQuantitySelection={restrictProductQuantitySelection}
              />
            </>
          )}
        </Box>
      )}
    </>
  );
}
